import React, { useEffect, useMemo, useState } from "react"
import Typography from "aldoo-ra/Typography"
import { Squares2X2Icon, ListBulletIcon, MagnifyingGlassIcon, SparklesIcon, EyeIcon, StarIcon, FireIcon, BarsArrowDownIcon, BarsArrowUpIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import ProductBox from "./ProductBox"
import ProductRow from "./ProductRow"
import Pagination from "./Pagination"
import Spinner from "./Spinner"
import useT from "../hooks/useT"
import { CartPlacement } from "../store/placements"
import useAsync from "../hooks/useAsync"
import useSiteLanguage from "../hooks/useSiteLanguage"
import useSearchParams, { setSearchParams } from "../hooks/useSearchParams"
import { ListProducts, LIST_FIELDS } from "../api"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { usePageContext } from "vike-react/usePageContext"
import { catalogSeedKey, CATALOG_PAGE_SIZE, FIRST_ROW } from "./catalogSeed"

const ITEMS_PER_PAGE = CATALOG_PAGE_SIZE
const VIEW_KEY = "categoryBrowserViewMode"

/**
 * Paged product browser for a category subtree, a content type, a text
 * search or one seller's products (`publisher`: its slug or company id). Sort
 * and page live in the URL (?sortBy=&page=), 1-based page.
 */
export default function CategoryItemBrowser({ categories, contentTypes, textSearch, publisher, showTitle = false, title, placement = null, seedFor = null }) {
  const t = useT()
  const language = useSiteLanguage()
  const params = useSearchParams()
  const sortBy = params.get("sortBy") || (textSearch ? "relevance" : "published")
  const page = Math.max(1, parseInt(params.get("page")) || 1)
  const [viewMode, setViewMode] = useState("grid")

  useEffect(() => {
    try {
      const saved = localStorage.getItem(VIEW_KEY)
      if (saved) setViewMode(saved)
    } catch {}
  }, [])

  const categoryArray = useMemo(() => (Array.isArray(categories) ? categories : categories ? [categories] : []), [categories])
  const contentTypesArray = useMemo(() => (Array.isArray(contentTypes) ? contentTypes : contentTypes ? [contentTypes] : []), [contentTypes])
  const categoriesKey = categoryArray.join(",")
  const contentTypesKey = contentTypesArray.join(",")

  // the first page the server rendered for this list (catalogSSR), when it is this one
  const pageContext = usePageContext()
  const seed = pageContext?.storefrontCategoryList
  const initial = seedFor && seed?.key === catalogSeedKey({ seedFor, sortBy, page, textSearch: textSearch || "", language }) ? seed.result : null

  const { result, pending } = useAsync(
    () => {
      const request = {
        page: page - 1,
        items_per_page: ITEMS_PER_PAGE,
        language,
        sortBy,
        sortDirection: -1,
        owned_only: false,
        fields: LIST_FIELDS,
      }
      // a search is never empty: nothing matching, the API answers the closest it finds (or the popular), and says so
      if (textSearch) Object.assign(request, { text_search: textSearch, fallback: true })
      if (publisher) request.publisher = publisher
      if (categoryArray.length === 1) request.category = categoryArray[0]
      else if (categoryArray.length > 1) request.categories = categoryArray
      if (contentTypesArray.length === 1) request.contentType = contentTypesArray[0]
      else if (contentTypesArray.length > 1) request.contentTypes = contentTypesArray
      return ListProducts(request)
    },
    [categoriesKey, contentTypesKey, textSearch, publisher, sortBy, page, language],
    { initial }
  )

  const changeSort = (value) => {
    setSearchParams({ sortBy: value, page: 1 })
    window.scrollTo({ top: 0, behavior: "smooth" })
  }
  const changePage = (p) => {
    setSearchParams({ page: p })
    window.scrollTo({ top: 0, behavior: "smooth" })
  }
  const changeView = (mode) => {
    setViewMode(mode)
    try {
      localStorage.setItem(VIEW_KEY, mode)
    } catch {}
  }

  const sortOptions = [
    // relevance means something only against a search text: a category has none
    ...(textSearch ? [{ value: "relevance", label: t`Relevance`, icon: MagnifyingGlassIcon }] : []),
    { value: "published", label: t`Newest`, icon: SparklesIcon },
    { value: "views", label: t`Most Viewed`, icon: EyeIcon },
    { value: "avg_rating", label: t`Highest Rated`, icon: StarIcon },
    { value: "num_sold", label: t`Bestsellers`, icon: FireIcon },
    { value: "price_low", label: t`Lowest Price`, icon: BarsArrowDownIcon },
    { value: "price_high", label: t`Highest Price`, icon: BarsArrowUpIcon },
  ]

  const sortRef = React.useRef(null)
  const [sortRoom, setSortRoom] = useState(480)
  const list = result?.list || []
  const toggle = (mode, Icon, label) => (
    <button
      type="button"
      aria-label={label}
      aria-pressed={viewMode === mode}
      onClick={() => changeView(mode)}
      className={twMerge("w-10 h-10 inline-flex items-center justify-center", viewMode === mode ? "bg-theme_black text-white" : "text-text hover:bg-neutral")}
    >
      <Icon className="w-5 h-5" />
    </button>
  )

  return (
    <CartPlacement value={placement}>
    <div className="w-full">
      {/* <hr className="border-bordered mb-4" /> */}
      {/* one line, on a phone too: the sort takes the room, the view toggle the end */}
      <div className="flex flex-row flex-nowrap items-center justify-between gap-3 md:gap-4 mb-4">
        <div className="flex flex-1 min-w-0 items-center gap-4">
          {showTitle && <Typography 
                          component="h1"
                          variant="h4"
                          className="leading-none"
                        >   
            {title || t`Books`}
          </Typography>}
          {/* the framework dropdown, not the browser's own: a native select
              renders with the operating system's own menu, which follows
              neither the site's theme nor its type */}
          {/* the menu as tall as its options when the screen has the room (all of them in view), else
              the room there is, scrolled — measured as it opens, above or below the button, whichever is larger */}
          <div
            ref={sortRef}
            className="w-full md:w-auto"
            onPointerDownCapture={() => {
              const rect = sortRef.current?.getBoundingClientRect()
              if (!rect) return
              setSortRoom(Math.max(160, Math.max(window.innerHeight - rect.bottom, rect.top) - 24))
            }}
          >
          <DropDownSelect
            options={sortOptions}
            maxHeight={`${Math.min(sortOptions.length * 48 + 8, sortRoom)}px`}
            value={sortOptions.filter((o) => o.value === sortBy)}
            onChange={([option]) => option && changeSort(option.value)}
            variant="aldoo"
            allowEmptySelection={false}
            // a sort always has a value: no reset cross, the chevron instead
            enableResetButton={false}
            placeholder={t`Sort By`}
            // each way to sort with its icon, in the list and on the button; the chosen one ticked, no checkboxes
            showCheckbox={false}
            showSelectionTick
            onItemRender={(option) => {
              const Icon = option?.icon
              return (
                <span className="flex items-center gap-3 min-w-0">
                  {Icon && <Icon className="w-5 h-5 shrink-0" />}
                  <span className="truncate">{option?.label}</span>
                </span>
              )
            }}
            // wide enough for the longest option ("Най-висока оценка") on one
            // line; on a phone it takes what the toggle leaves
            className="w-full md:w-auto md:min-w-[250px]"
            // rows tall enough to tap (48px)
            styleOverrides={{
              menuItem: "flex items-center min-h-[48px] text-text px-4 py-3 hover:bg-canvas cursor-pointer whitespace-nowrap",
              menuItemSelected: "flex items-center min-h-[48px] px-4 py-3 bg-primary/10 cursor-pointer text-primary font-medium transition-colors whitespace-nowrap",
              // the height is the maxHeight above, not the variant's fixed cap
              menuContainer: "rounded-lg overflow-hidden overflow-y-auto scrollbar-thin scrollbar-thumb-neutral_2 scrollbar-track-paper",
            }}
          />
          </div>
        </div>

        {/* the list layout is the desktop's: a phone is always the grid, and the sort takes the whole line */}
        <div className="hidden md:inline-flex shrink-0 rounded-md border border-bordered overflow-hidden">
          {toggle("grid", Squares2X2Icon, "grid view")}
          {toggle("list", ListBulletIcon, "list view")}
        </div>
      </div>
      <hr className="border-bordered mb-6" />

      {/* the search answered by something close to it: what it shows instead, said before the products */}
      {textSearch && result?.searchFallback && list.length > 0 && (
        <p className="mb-6 text-[15px] text-text_secondary" data-testid="search-fallback">
          {result.searchFallback.mode === "popular"
            ? t("No matches for “%query%”. Here are our popular products.", textSearch)
            : ["shortened", "fewer_words"].includes(result.searchFallback.mode)
              ? t("No exact matches for “%query%”. Showing results for “%shown%”.", textSearch, result.searchFallback.query)
              : t("No exact matches for “%query%”. Showing similar results.", textSearch)}
        </p>
      )}

      {result && pending && (
        <div className="flex justify-center py-2" data-testid="catalog-loading" aria-busy="true">
          <Spinner />
        </div>
      )}
      {!result && pending ? (
        <Spinner />
      ) : list.length === 0 ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <p className="text-[18px] text-text_secondary">{t`No products found`}</p>
        </div>
      ) : viewMode === "list" ? (
        <div className="flex flex-col gap-3 md:gap-4 mb-8">
          {list.map((product) => (
            <ProductRow key={product.pid} product={product} />
          ))}
        </div>
      ) : null}

      {list.length > 0 && (
        <div className={twMerge("flex flex-wrap justify-evenly md:justify-center gap-1 md:gap-5 mb-8", viewMode === "list" && "hidden")}>
          {list.map((product, i) => (
            <ProductBox key={product.pid} product={product} variant="portrait" priority={page === 1 && i < FIRST_ROW} />
          ))}
        </div>
      )}

      {result?.pages > 1 && <Pagination page={page} count={result.pages} onChange={changePage} className="mt-8" />}
    </div>
    </CartPlacement>
  )
}
